import React, { useState } from "react";
import { API_URL } from "../config/apiConfig";
import axios from "axios";
import { HiOutlineMailOpen, HiPhone } from "react-icons/hi";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Phone } from "lucide-react";

const Contact = ({ source }) => {
  const navigate = useNavigate();
  
  // Visa services list
  const visaServices = [
    "General Skilled Migration Visas",
    "Parent Visas",
    "Business & Investment Visas",
    "Student Visas",
    "Employment Sponsored Visas",
    "Partner Visas",
    "Temporary Graduate Visas",
    "Others",
    "Administrative Appeal Tribunal AAT Applications"
  ];

  // Initialize formData state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    nationality: "",
    currentlyInAustralia: "",
    holdingAustralianVisa: "",
    interestedVisaService: "",
    message: "",
    source: source || "", // Include the source if provided
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log("Sending request to:", `${API_URL}/send-email`);
      const response = await axios.post(`${API_URL}/send-email`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        nationality: formData.nationality,
        currentlyInAustralia: formData.currentlyInAustralia,
        holdingAustralianVisa: formData.holdingAustralianVisa,
        interestedVisaService: formData.interestedVisaService,
        message: formData.message,
      });

      console.log("Email sent successfully:", response.data.message);
      setStatus({ type: "success", message: "Message sent successfully!" });
      
      // Redirect to thank you page
      navigate("/thank-you");
    } catch (error) {
      console.error("Error sending email:", error);
      setStatus({
        type: "error",
        message: error.response?.data?.message || "Failed to send message",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="contact-section" className="container max-w-1440px mx-auto mb-10 px-4 sm:px-8 py-10 lg:px-14 relative">
      <div className="grid xl:grid-cols-2 xl:gap-6">
        {/* Left Section (Form) */}
        <div className="xl:h-[680px] hellix-font bg-shape p-4 py-6 md:py-10 md:p-10 rounded-lg">
          <div className="md:text-left text-center mb-10">
            <h1 className="font-semibold hellix-font text-[26px] sm:text-[36px] text-blue">
              Get Expert Assistance – Let's Talk!
            </h1>
            <h2 className="font-medium hellix-font text-lg sm:text-2xl text-blue mt-2">
              You can reach us anytime
            </h2>
          </div>

          {/* Form */}
          <form className="w-full space-y-3" onSubmit={handleSubmit}>
            {/* Name */}
            <div>
              <input
                className="rounded-[14px] w-full h-[60px] bg-bginput text-base font-medium text-[#8D8B8B] placeholder:text-[#8D8B8B] pl-4 focus:outline-none"
                name="name"
                type="text"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Email and Phone in one row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative w-full">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue w-[18px] h-[14px]" />
                <input
                  className="w-full pl-10 pr-4 h-[60px] hellix-font rounded-[14px] bg-bginput text-base font-medium text-[#8D8B8B] placeholder:text-[#8D8B8B] focus:outline-none"
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="relative w-full">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue w-[18px] h-[14px]" />
                <input
                  className="w-full pl-10 h-[60px] rounded-[14px] hellix-font bg-bginput text-base font-medium text-[#8D8B8B] placeholder:text-[#8D8B8B] focus:outline-none"
                  name="phoneNumber"
                  placeholder="Phone Number"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Nationality and Australia Status in one row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="w-full">
                <input
                  className="w-full h-[60px] rounded-[14px] bg-bginput text-base font-medium text-[#8D8B8B] placeholder:text-[#8D8B8B] pl-4 focus:outline-none"
                  name="nationality"
                  type="text"
                  placeholder="Nationality"
                  value={formData.nationality}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="w-full">
                <select
                  className="w-full h-[60px] rounded-[14px] bg-bginput text-base font-medium text-[#8D8B8B] pl-4 focus:outline-none"
                  name="currentlyInAustralia"
                  value={formData.currentlyInAustralia}
                  onChange={handleChange}
                  required
                >
                  <option value="">Currently in Australia?</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>

            {/* Visa Status and Service Interest in one row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="w-full">
                <select
                  className="w-full h-[60px] rounded-[14px] bg-bginput text-base font-medium text-[#8D8B8B] pl-4 focus:outline-none"
                  name="holdingAustralianVisa"
                  value={formData.holdingAustralianVisa}
                  onChange={handleChange}
                  required
                >
                  <option value="">Holding Australian Visa?</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div className="w-full">
                <select
                  className="w-full h-[60px] rounded-[14px] bg-bginput text-base font-medium text-[#8D8B8B] pl-4 focus:outline-none"
                  name="interestedVisaService"
                  value={formData.interestedVisaService}
                  onChange={handleChange}
                  required
                >
                  <option value="">Interested Visa Service</option>
                  {visaServices.map((service) => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <textarea
                className="rounded-[14px] hellix-font w-full h-[100px] bg-bginput text-base font-medium text-[#8D8B8B] placeholder:text-[#8D8B8B] p-4 resize-none focus:outline-none"
                name="message"
                placeholder="How Can We Help?"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-2 hellix-font bg-background text-white text-lg font-medium h-[60px] rounded-[14px]"
              >
                {isSubmitting ? "Sending..." : "Submit"}
              </button>
            </div>

            {/* Status Messages */}
            {status.type === "success" && (
              <div className="text-green-600 text-center mt-4">
                {status.message}
              </div>
            )}
            {status.type === "error" && (
              <div className="text-red-600 text-center mt-4">
                {status.message}
              </div>
            )}
          </form>
        </div>

        {/* Right Section */}
        <div className="contact mt-6">
          <h1 className="text-[26px] max-xl:text-center text-blue hellix-font font-semibold text-right">
            Get Expert Guidance
          </h1>
          <h2 className="text-lg hellix-font sm:text-2xl text-blue font-semibold text-right">
            Email, Call or fill the form to reach us anytime!
          </h2>
          <div className="text-lg hellix-font mt-2 xl:mt-4 text-blue font-normal text-right">
            <Link
              to="#"
              className="flex items-center justify-end"
              onClick={(e) => {
                window.location.href = "mailto:connect@immigetglobal.com";
                e.preventDefault();
              }}
            >
              <span>connect@immigetglobal.com</span>
              <span>
                <HiOutlineMailOpen className="ml-2 text-blue md:w-[22px] md:h-[16px] w-[30px] h-[20px]" />
              </span>
            </Link>
          </div>

          <div className="text-lg hellix-font mt-2 xl:mt-4 text-blue font-normal text-right">
            <Link
              to="#"
              className="flex items-center justify-end"
              onClick={(e) => {
                window.location.href = "tel:+610370658787";
                e.preventDefault();
              }}
            >
              <span>+61 03 7065 8787</span>
              <span>
                <HiPhone className="ml-2 text-blue md:w-[22px] md:h-[16px] w-[30px] h-[20px]" />
              </span>
            </Link>
          </div>

          <div className="mt-3 flex flex-wrap relative rounded-lg w-full">
            <div className="absolute top-0 -z-0 w-full h-full hidden xl:block">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d692.9055077982354!2d145.03544877732327!3d-37.821037740431976!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad64315619ebc9b%3A0x68b5d939c2c94990!2sImmiget%20Global%20-%20Most%20Trusted%20Migration%20Agents%20In%20Melbourne%2C%20Australia!5e1!3m2!1sen!2sin!4v1747650722710!5m2!1sen!2sin"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-[450px]"
                alt=""
                title="map_one"
              />
            </div>

            {/* Content Over the Map */}
            <div
              className="rounded-s-lg truncate w-full z-10 text-white p-6 xl:absolute xl:w-[48%] xl:right-0 xl:h-[450px] top-0 flex flex-col gap-4 xl:gap-8 my-auto justify-center"
              style={{
                background:
                  "linear-gradient(127.59deg, rgba(226, 242, 235, 0.54) 90.72%, rgba(219, 247, 250, 0.54) 100%)",
                backdropFilter: "blur(6px)",
              }}
            >
              <h1 className="font-semibold hellix-font text-[42px] text-blue xl:pb-2 text-right">
                Our Location
              </h1>
              <h2 className="font-medium hellix-font text-2xl text-blue text-right">
                Connecting Near and Far
              </h2>
              <p className="font-semibold hellix-font text-xl text-blue py-2 xl:py-2 text-right">
                HEADQUARTERS:
                <br />
                Suite 104/672 Glenferrie Road,
                <br />
                Hawthorn 3122,
                <br />
                Australia
              </p>
            </div>
            <div className="-z-0 w-full h-[] xl:hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d692.9055077982354!2d145.03544877732327!3d-37.821037740431976!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad64315619ebc9b%3A0x68b5d939c2c94990!2sImmiget%20Global%20-%20Most%20Trusted%20Migration%20Agents%20In%20Melbourne%2C%20Australia!5e1!3m2!1sen!2sin!4v1747650722710!5m2!1sen!2sin"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-[250px] md:h-[400px]"
                alt=""
                title="map_two"
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Contact;